import { fetchWithAuth, getJson, ApiError, readBody } from "./client";

// Workflow lifecycle calls — list, submit, watch, cancel, download
// links — that back the export-wizard and workflow-detail pages.

export type WorkflowStatus =
  | "running"
  | "succeeded"
  | "failed"
  | "cancelled";

// MUI Chip colors for each status. Shared by every page that renders a
// status chip (ExportsList, ClusterActivity, ExportDetail) so the
// vocabulary stays consistent.
export const STATUS_COLOR: Record<
  WorkflowStatus,
  "default" | "primary" | "success" | "error" | "warning"
> = {
  running: "primary",
  succeeded: "success",
  failed: "error",
  cancelled: "warning",
};

export interface WorkflowRow {
  id: string;
  kind: string;
  params_json: string;
  status: WorkflowStatus;
  current_stage: number;
  submitted_by: string;
  submitted_at: number;  // ms epoch
  // Set when the orchestrator first picks the workflow up. Often
  // identical to submitted_at for fast-start workflows; the gap
  // matters when the cluster's queued (concurrency cap saturated).
  started_at: number | null;
  finished_at: number | null;
  error_summary: string | null;
  // v10: managed-retention bookkeeping for export-kind workflows.
  // Null on non-export kinds AND on pre-v10 export workflows the
  // operator never registered (the reaper, when added, ignores
  // null-output_expires_at rows). When populated, the UI surfaces
  // an "Output expires in …" chip + Extend button.
  output_expires_at?: number | null;
  output_deleted_at?: number | null;
  output_size_bytes?: number | null;
}

export interface WorkflowJob {
  id: string;
  kind: string;
  // Per-job params (input URI, start/end date for export, etc.) used
  // by the diagram to derive a YYYY-MM label per job.
  params_json: string;
  k8s_job_name: string | null;
  // Container image the Job was launched with — populated at submit
  // by the K8sOrchestrator. Surfaced under the job name in the
  // workflow detail UI so operators can confirm which paim tag
  // ran a given Job (useful across rollouts).
  image: string | null;
  status: string;
  stage_index: number;
  progress_completed: number | null;
  progress_total: number | null;
  progress_message: string | null;
  counters_json: string | null;
  started_at: number | null;
  finished_at: number | null;
  error_summary: string | null;
}

export interface WorkflowDetail extends WorkflowRow {
  jobs: WorkflowJob[];
}

export interface ListWorkflowsResponse {
  workflows: WorkflowRow[];
  limit: number;
  offset: number;
}

export interface BatchExportParams {
  bucket: string;
  months: string[];
  filter: string;
  output: string;
  content_types?: string[]; // corpora to export; omitted ⇒ broadcast
  search?: string;
  workers?: number;
  concurrency?: number;
  // Format the export emits. Operator-side default (when omitted) is
  // ``xml`` → xml-zip. ``condensed-json`` is the one that unlocks the
  // search-env feature: each emitted ndjson record is a per-program
  // superdoc that loads cleanly into OpenSearch.
  // "original" (raw source docs zipped; "xml"/"xml-zip"/"zip" are
  // accepted legacy aliases server-side) | "ndjson" | "condensed-json".
  output_format?: string;
  // shard_bytes dropped in 0.31 — the C# exporter produces one
  // output object regardless of size, so there's nothing to shard.
  // One-or-two-sentence summary entered on the wizard's Review step,
  // optionally AI-pre-filled. Persisted on the workflow row as
  // params.description and surfaced on the dataset detail page.
  description?: string;
}

export interface SubmitWorkflowResponse {
  workflow_id: string;
  status: string;
}

export interface DownloadPart {
  // Raw S3 basename (e.g. "all.zip", "manifest.parquet"). Kept for
  // diagnostics; the UI uses download_name as the display label.
  name: string;
  url: string;
  // What S3 actually serves the file as (via Content-Disposition on
  // the presigned URL). Typically "paim-<workflow>-<YYYY-MM>-all.zip"
  // — distinct per month so multiple downloads don't clobber.
  download_name?: string;
}

export interface DownloadBundle {
  expires_in: number;
  manifest_url?: string;
  manifest_download_name?: string;
  index_url?: string;
  index_download_name?: string;
  parts?: DownloadPart[];
  // batch_export shape: months grouped
  months?: {
    month: string;
    manifest_url: string;
    manifest_download_name?: string;
    index_url: string;
    index_download_name?: string;
    parts: DownloadPart[];
  }[];
}

export function listWorkflows(opts: {
  status?: WorkflowStatus;
  limit?: number;
  offset?: number;
} = {}): Promise<ListWorkflowsResponse> {
  const q = new URLSearchParams();
  if (opts.status) q.set("status", opts.status);
  if (opts.limit !== undefined) q.set("limit", String(opts.limit));
  if (opts.offset !== undefined) q.set("offset", String(opts.offset));
  const qs = q.toString() ? `?${q.toString()}` : "";
  return getJson<ListWorkflowsResponse>(`/api/v1/operator/workflows${qs}`);
}

export function getWorkflow(id: string): Promise<WorkflowDetail> {
  return getJson<WorkflowDetail>(`/api/v1/operator/workflows/${id}`);
}

export async function submitBatchExport(
  params: BatchExportParams,
): Promise<SubmitWorkflowResponse> {
  return submitWorkflow("batch_export", params);
}

export interface BatchInventoryParams {
  bucket: string;
  months: string[];
  content_type?: string; // "broadcast" (default) | "podcast"
  workers?: number;
  skip_existing?: boolean;
  rollup_cpu?: string;
  rollup_memory?: string;
}

export function submitBatchOnboard(
  params: BatchInventoryParams,
): Promise<SubmitWorkflowResponse> {
  return submitWorkflow("batch_onboard", params);
}

export function submitBatchRebuild(
  params: BatchInventoryParams,
): Promise<SubmitWorkflowResponse> {
  return submitWorkflow("batch_rebuild", params);
}

// year_onboard is a thin shim over batch_onboard that fills in
// months=['YYYY-01'..'YYYY-12'] for a calendar year. Useful for
// admin "onboard a whole year" actions where typing 12 months by
// hand is friction. Operator default concurrency is 6 (down from
// 12 in 0.28.5) — caller can override.
export interface YearOnboardParams {
  year: string;
  bucket: string;
  workers?: number;
  skip_existing?: boolean;
  concurrency?: number;
  months?: string[]; // optional subset, ['01','02',...]; omit for all 12
}

export function submitYearOnboard(
  params: YearOnboardParams,
): Promise<SubmitWorkflowResponse> {
  return submitWorkflow("year_onboard", params);
}

// Generic submitter shared by export / onboard / rebuild. The operator
// shape is uniform: `{kind, params}` → 202 with `{workflow_id, status}`.
async function submitWorkflow(
  kind: string,
  params: object,
): Promise<SubmitWorkflowResponse> {
  const res = await fetchWithAuth("/api/v1/operator/workflows", {
    method: "POST",
    body: JSON.stringify({ kind, params }),
  });
  if (res.status !== 202) {
    throw new ApiError(
      res.status,
      await readBody(res),
      `submit returned ${res.status}`,
    );
  }
  return (await res.json()) as SubmitWorkflowResponse;
}

export async function cancelWorkflow(id: string): Promise<void> {
  const res = await fetchWithAuth(`/api/v1/operator/workflows/${id}`, {
    method: "DELETE",
  });
  if (!res.ok) {
    throw new ApiError(res.status, await readBody(res), `cancel returned ${res.status}`);
  }
}

// Admin-only — adjust mutable workflow params (concurrency, mostly)
// while the workflow is running. Backed by PATCH on the operator. The
// orchestrator picks up the new value at its next "should I start
// another job?" check; already-running jobs are not preempted.
export async function patchWorkflow(
  id: string,
  patch: { concurrency?: number },
): Promise<WorkflowDetail> {
  const res = await fetchWithAuth(`/api/v1/operator/workflows/${id}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    throw new ApiError(res.status, await readBody(res), `patch returned ${res.status}`);
  }
  return (await res.json()) as WorkflowDetail;
}

// Owner or admin — extend an export's output_expires_at by N hours,
// clamped at submitted_at + 30 days. Refuses on unmanaged workflows
// (null output_expires_at) and on non-export kinds. Response is the
// updated workflow row + granted/requested hours so the UI can be
// honest when a request got clamped at the ceiling.
export interface ExtendOutputRetentionResponse extends WorkflowDetail {
  granted_hours: number;
  requested_hours: number;
}

// Owner-or-admin — force-delete the output S3 prefix for a managed
// export workflow. Mirrors the reaper's safety: rejects unmanaged
// workflows, non-export kinds, and already-deleted outputs. Sets
// output_deleted_at on success; the workflow row itself sticks
// around so cost / counters / event-log audit stays intact.
export interface DeleteOutputResponse {
  workflow_id: string;
  prefix: string;
  deleted_objects: number;
}

export async function deleteWorkflowOutput(
  id: string,
): Promise<DeleteOutputResponse> {
  const res = await fetchWithAuth(`/api/v1/operator/workflows/${id}/output`, {
    method: "DELETE",
  });
  if (!res.ok) {
    throw new ApiError(
      res.status,
      await readBody(res),
      `delete output returned ${res.status}`,
    );
  }
  return (await res.json()) as DeleteOutputResponse;
}

export async function patchOutputRetention(
  id: string,
  hours: number,
): Promise<ExtendOutputRetentionResponse> {
  const res = await fetchWithAuth(
    `/api/v1/operator/workflows/${id}/output-retention`,
    {
      method: "PATCH",
      body: JSON.stringify({ hours }),
    },
  );
  if (!res.ok) {
    throw new ApiError(
      res.status,
      await readBody(res),
      `extend output retention returned ${res.status}`,
    );
  }
  return (await res.json()) as ExtendOutputRetentionResponse;
}

// Shape returned by /api/v1/operator/workflows/{id}/manifest. Mirrors
// the operator-side response in src/paim/operator/routes/_workflows.py
// (api_workflow_manifest). The unit "page" or "superdoc" determines
// what each ``total`` counts; counters / filter / time_range are
// aggregates derived from the per-month _manifest.json sidecars.
export interface ManifestMonth {
  month: string | null;          // YYYY-MM, or null on month_export
  manifest: ManifestMonthBody | null;  // null when the sidecar wasn't found in S3
}

export interface ManifestMonthBody {
  version: string;               // "manifest/v1"
  generated_at: string;
  output: { uri: string; format: string; unit: string; files: number; size_bytes: number };
  window: { start: string; end: string };
  filter: { expression: string; lucene_query?: string; transcript_regex?: string };
  matched: {
    total: number;
    distinct_stations: number;
    distinct_programs: number;
    total_duration_sec: number;
    total_page_count: number;
    total_line_count: number;
    total_ad_page_count: number;
    total_transcript_chars: number;
    time_range?: { min_start_utc?: string; max_end_utc?: string };
  };
  by_station: { source_name: string; count: number }[];
  by_category: { category: string; count: number }[];
  by_day: { date: string; count: number }[];
}

export interface WorkflowManifest {
  workflow_id: string;
  kind: string;
  submitted_by: string | null;
  submitted_at: number;
  output: { uri: string; format: string | null; unit: string | null };
  filter: { expression?: string; lucene_query?: string; transcript_regex?: string } | null;
  totals: {
    total: number;
    total_duration_sec: number;
    total_page_count: number;
    total_line_count: number;
    total_ad_page_count: number;
    total_transcript_chars: number;
    distinct_stations_seen: number;
    distinct_programs_seen: number;
    months_with_hits: number;
    months_empty: number;
    months_missing_manifest: number;
  };
  time_range: { min_start_utc?: string; max_end_utc?: string };
  by_station: { source_name: string; count: number }[];
  by_category: { category: string; count: number }[];
  by_day: { date: string; count: number }[];
  months: ManifestMonth[];
}

export function getWorkflowManifest(id: string): Promise<WorkflowManifest> {
  return getJson<WorkflowManifest>(`/api/v1/operator/workflows/${id}/manifest`);
}

// Full build_stages output replayed for the SPA. Deterministic for
// the workflow's params_json, so callers can fetch once and reuse for
// the lifetime of the page rather than polling. The portal merges
// these specs against the actual ``jobs`` rows so the UI shows the
// complete plan from t=0 — pending months render as ``planned``
// pseudo-rows rather than appearing one at a time as concurrency
// slots free up.
export interface WorkflowPlanSpec {
  stage_index: number;
  position: number;
  kind: string;
  params: Record<string, unknown>;
  // Canonical handle the SPA pairs against an actual job row. For
  // export specs this is ``params.start[:7]`` (YYYY-MM), matching what
  // the SPA's Month column shows. Empty string for kinds that don't
  // have a unique-within-stage identifier yet (rollup, onboard);
  // those fall back to position-based ordering.
  match_key: string;
}

export interface WorkflowPlan {
  workflow_id: string;
  concurrency: number;
  specs: WorkflowPlanSpec[];
}

export function getWorkflowPlan(id: string): Promise<WorkflowPlan> {
  return getJson<WorkflowPlan>(`/api/v1/operator/workflows/${id}/plan`);
}

export function getDownloadUrls(
  id: string,
  expires?: number,
): Promise<DownloadBundle> {
  const qs = expires ? `?expires=${expires}` : "";
  return getJson<DownloadBundle>(
    `/api/v1/operator/workflows/${id}/download-urls${qs}`,
  );
}
